import { useParams, useSearchParams } from "react-router-dom";
import { Github, KeyRound } from "lucide-react";
import { signInUrl, useInviteLookup, useSignInProviders } from "@/api/hooks";
import { errorMessage } from "@/api/client";
import type { PublicInvite } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Mark } from "@/components/Mark";
import { formatDateTime } from "@/lib/utils";
import { describeAuthError } from "./SignIn";

const UNUSABLE: Record<Exclude<PublicInvite["status"], "active">, string> = {
  invalid: "This invite link is not valid. Check that you copied all of it, or ask for a new one.",
  used: "This invite has already been used. Ask whoever invited you for a new link.",
  expired: "This invite has expired. Ask whoever invited you for a new link.",
  revoked: "This invite was withdrawn. Ask whoever invited you for a new link.",
};

/**
 * `/invite/<token>`: says who invited you and starts the normal sign-in with the
 * token attached. The API creates the account in the callback, or sends the
 * browser back here with `?error=invite_<reason>`.
 */
export default function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const invite = useInviteLookup(token);
  const providers = useSignInProviders();
  const error = describeAuthError(params.get("error"));
  const list = providers.data?.providers ?? [];
  const data = invite.data;

  return (
    <div className="flex min-h-app items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardContent className="py-8">
          <div className="flex items-center gap-2">
            <Mark className="size-10 shrink-0" />
            <span className="text-lg font-semibold tracking-tight">Minne</span>
          </div>

          {invite.isLoading && <p className="mt-5 text-sm text-muted-foreground">Checking your invite…</p>}
          {invite.isError && (
            <p role="alert" className="mt-5 text-sm text-destructive">
              {errorMessage(invite.error, "Could not check the invite.")}
            </p>
          )}

          {data && (
            <h1 className="mt-5 text-xl font-semibold tracking-tight">
              {data.status === "active" ? `${data.invitedBy ?? "Someone"} invited you to Minne` : "Invite not usable"}
            </h1>
          )}

          {error && (
            <p role="alert" className="mt-3 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}

          {data && data.status !== "active" && <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{UNUSABLE[data.status]}</p>}

          {data?.status === "active" && (
            <>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Minne keeps the notes your AI assistants write. Sign in to create your account; you get your own space right away.
                {data.emailLocked && " This invite is for a specific email address, so sign in with the account that uses it."}
              </p>
              {list.length > 0 ? (
                <div className="mt-6 flex flex-col gap-2">
                  {list.map((p) => (
                    <Button key={p.id} asChild size="lg" variant={p.id === "github" ? "default" : "outline"}>
                      <a href={signInUrl(p.id, { invite: token })}>
                        {p.id === "github" ? <Github /> : <KeyRound />} Continue with {p.name}
                      </a>
                    </Button>
                  ))}
                </div>
              ) : (
                providers.data && <p className="mt-4 text-sm text-muted-foreground">This server has no sign-in method that accepts new accounts.</p>
              )}
              {data.expiresAt && <p className="mt-4 text-xs text-muted-foreground">The invite is valid until {formatDateTime(data.expiresAt)}.</p>}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
